import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authApi } from '../auth/api';
import { useAuthStore } from '../auth/useAuthStore';
import { GuestLoginPanel } from '../dashboard/components/GuestLoginPanel';
import type { SceneMode } from '../dashboard/components/ProjectTerritorialScene';
import { ProjectTerritorialScene } from '../dashboard/components/ProjectTerritorialScene';
import { ShellHeader } from '../dashboard/components/ShellHeader';
import { TerritorialDock } from '../dashboard/components/TerritorialDock';
import { useProject } from './ProjectContext';
import { ProjectCoverMedia } from './ProjectCoverMedia';
import { type PublicProject, publicProjectsApi } from './public-projects-api';
import '../dashboard/project-dashboard.css';
import './ProjectEntryPage.css';

const ACTIVE_PROJECT_KEY = 'wayline-active-project-id';

function persistDevToken(accessToken: string): void {
  localStorage.setItem(
    'auth-storage',
    JSON.stringify({
      state: { token: accessToken },
      version: 0,
    })
  );
}

function readStoredSceneMode(): SceneMode {
  const v = localStorage.getItem('ts-scene-mode');
  if (v === 'light') return 'light';
  if (v === 'topographic') return 'topographic';
  if (v === 'satellite') return 'satellite';
  return 'dark';
}

// ─── Loading ──────────────────────────────────────────────────────────────────

function ProjectEntryLoading(): JSX.Element {
  return (
    <div className="pe-fullscreen pe-fullscreen--dark">
      <div className="pe-loader">
        <div className="pe-spinner" />
      </div>
    </div>
  );
}

// ─── Sin acceso ───────────────────────────────────────────────────────────────

function ProjectAccessDenied({ projectName }: { projectName: string }): JSX.Element {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  return (
    <div className="pe-fullscreen pe-fullscreen--dark">
      <div className="pe-notfound">
        <div className="pe-notfound-code" style={{ fontSize: 32 }}>
          Sin acceso
        </div>
        <p className="pe-notfound-title">No tenés acceso a este proyecto</p>
        <p className="pe-notfound-desc">
          Tu cuenta no tiene permisos para ver <strong>{projectName}</strong>.
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 24,
            alignItems: 'center',
          }}
        >
          <button className="pe-access-btn" onClick={() => navigate('/territorio')}>
            Volver a mis proyectos
          </button>
          <button className="pe-access-btn pe-access-btn--secondary" onClick={handleLogout}>
            Cambiar de cuenta
          </button>
          <button className="pe-access-btn pe-access-btn--ghost" disabled>
            Solicitar acceso
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 404 ─────────────────────────────────────────────────────────────────────

function ProjectEntryNotFound(): JSX.Element {
  return (
    <div className="pe-fullscreen pe-fullscreen--dark">
      <div className="pe-notfound">
        <div className="pe-notfound-code">404</div>
        <p className="pe-notfound-title">Proyecto no encontrado</p>
        <p className="pe-notfound-desc">
          El enlace puede haber cambiado o el proyecto no está disponible públicamente.
        </p>
      </div>
    </div>
  );
}

// ─── Shell unificado ─────────────────────────────────────────────────────────

interface TerritorialShellProps {
  pubProject: PublicProject;
}

// Guard: detecta acceso denegado antes de montar el shell completo
function TerritorialShell({ pubProject }: TerritorialShellProps): JSX.Element {
  const { isAuthenticated } = useAuthStore();
  const { availableProjects, isLoading: projectsLoading } = useProject();
  const activeProject = availableProjects.find(p => p.id === pubProject.id) ?? null;

  if (isAuthenticated && !projectsLoading && !activeProject) {
    return <ProjectAccessDenied projectName={pubProject.name} />;
  }

  return <TerritorialShellInner pubProject={pubProject} activeProject={activeProject} />;
}

interface TerritorialShellInnerProps {
  pubProject: PublicProject;
  activeProject: import('./types').Project | null;
}

function useDevQuickLogin(pubProjectId: string, isAuthenticated: boolean): void {
  const [searchParams] = useSearchParams();
  const shouldUseDevQuickLogin = searchParams.get('devQuickLogin') === '1';
  const [devLoginAttempted, setDevLoginAttempted] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (isAuthenticated) return;
    if (!shouldUseDevQuickLogin) return;
    if (devLoginAttempted) return;

    let cancelled = false;
    setDevLoginAttempted(true);

    void (async (): Promise<void> => {
      try {
        const devUsers = await authApi.getDevUsers();
        const matchedUser = devUsers.find(devUser =>
          devUser.projects.some(project => project.id === pubProjectId)
        );

        if (!matchedUser || cancelled) return;

        const response = await authApi.devQuickLogin(matchedUser.userId);
        if (cancelled) return;

        persistDevToken(response.accessToken);
        useAuthStore.setState({
          user: response.user,
          token: response.accessToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch {
        // If DEV quick-login is unavailable, keep the regular guest access flow visible.
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [devLoginAttempted, isAuthenticated, pubProjectId, shouldUseDevQuickLogin]);
}

function TerritorialShellInner({
  pubProject,
  activeProject,
}: TerritorialShellInnerProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();
  const { hasProjects, setProjectId } = useProject();
  const [sceneMode, setSceneMode] = useState<SceneMode>(readStoredSceneMode);
  const [sceneError, setSceneError] = useState(false);
  const requestedWorkspace = searchParams.get('workspace');
  useDevQuickLogin(pubProject.id, isAuthenticated);

  const isSuperAdmin = user?.role?.name === 'superadmin';

  const projectModules = activeProject?.activeModules;
  const userName = user ? `${user.firstName} ${user.lastName}` : '';

  // Al autenticarse, activar el proyecto correcto en el contexto
  useEffect(() => {
    if (isAuthenticated && pubProject.id) {
      localStorage.setItem(ACTIVE_PROJECT_KEY, pubProject.id);
      setProjectId(pubProject.id);
    }
  }, [isAuthenticated, pubProject.id, setProjectId]);

  useEffect(() => {
    if (!isAuthenticated || !activeProject) return;
    if (requestedWorkspace !== 'inventory') return;
    navigate('/workspaces/inventory', { replace: true });
  }, [activeProject, isAuthenticated, navigate, requestedWorkspace]);

  const onSceneModeChange = useCallback((mode: SceneMode): void => {
    setSceneMode(mode);
    localStorage.setItem('ts-scene-mode', mode);
  }, []);

  // En guest mode: cover. En auth: escena almacenada.
  const activeSceneMode: SceneMode = isAuthenticated ? sceneMode : 'cover';

  return (
    <div
      className={`ts-layout pe-shell${isAuthenticated ? ' pe-shell--auth' : ' pe-shell--guest'}`}
    >
      {/* Escena territorial — siempre presente, misma instancia */}
      {!sceneError && (
        <ProjectTerritorialScene
          projectId={pubProject.id}
          bounds={pubProject.bounds}
          sceneMode={activeSceneMode}
          onSceneError={() => setSceneError(true)}
        />
      )}

      {/* ── GUEST MODE: portada visual + formulario de acceso ── */}
      {!isAuthenticated && (
        <>
          <ProjectCoverMedia
            slug={pubProject.slug}
            coverVideoUrl={pubProject.coverVideoUrl}
            coverPosterUrl={pubProject.coverPosterUrl}
            coverImageUrl={pubProject.coverImageUrl}
          />
          <GuestLoginPanel projectName={pubProject.name} territory={pubProject.territory} />
        </>
      )}

      {/* ── AUTH MODE: navegación completa ── */}
      {isAuthenticated && (
        <>
          <ShellHeader
            userName={userName}
            projectName={activeProject?.name ?? pubProject.name}
            sceneMode={sceneMode}
            onSceneModeChange={onSceneModeChange}
          />
          <TerritorialDock
            hasProjects={hasProjects}
            projectModules={projectModules}
            isSuperAdmin={isSuperAdmin}
          />
        </>
      )}
    </div>
  );
}

// ─── Página pública por slug ──────────────────────────────────────────────────

type Status = 'loading' | 'ready' | 'not-found' | 'error';

interface ProjectEntryPageProps {
  /** Slug de URL (/p/:slug). Si se omite, se extrae de useParams. */
  initialSlug?: string;
  /**
   * Cuando es true, usa `getBySubdomain` en lugar de `getBySlug`.
   * Necesario en modo subdominio donde el subdominio puede diferir del slug interno.
   * Ejemplo: sanjuan.wayline.city → public_subdomain = 'sanjuan', slug interno = 'san-juan-irpha'.
   */
  useSubdomainLookup?: boolean;
}

export function ProjectEntryPage({
  initialSlug,
  useSubdomainLookup = false,
}: ProjectEntryPageProps = {}): JSX.Element {
  const params = useParams<{ projectSlug: string }>();
  const projectSlug = initialSlug ?? params.projectSlug;
  const [status, setStatus] = useState<Status>('loading');
  const [pubProject, setPubProject] = useState<PublicProject | null>(null);

  useEffect(() => {
    if (!projectSlug) {
      setStatus('not-found');
      return;
    }
    let cancelled = false;
    const fetchFn = useSubdomainLookup
      ? publicProjectsApi.getBySubdomain(projectSlug)
      : publicProjectsApi.getBySlug(projectSlug);

    fetchFn
      .then((data): void => {
        if (!cancelled) {
          setPubProject(data);
          setStatus('ready');
        }
      })
      .catch((err: unknown): void => {
        if (!cancelled) {
          const code = (err as { response?: { status?: number } })?.response?.status;
          setStatus(code === 404 ? 'not-found' : 'error');
        }
      });
    return (): void => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug, useSubdomainLookup]);

  if (status === 'loading') return <ProjectEntryLoading />;
  if (status === 'not-found' || status === 'error') return <ProjectEntryNotFound />;
  if (!pubProject) return <ProjectEntryLoading />;
  return <TerritorialShell pubProject={pubProject} />;
}
